import React, { useCallback, useEffect, useState } from 'react';
import { MdCheck } from 'react-icons/md';
import { HiPlus } from 'react-icons/hi';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

import { Container } from './styles';

import { IModalityData } from '../../Modalities';
import Category from './Category';

export interface ICategoryData {
  input_id: number;
  id?: number;
  name?: string;
  order?: number;
  child?: boolean;
  elderly?: boolean;
  male?: boolean;
  female?: boolean;
  qty_min?: number;
  has_kit?: boolean;
  has_age_group?: boolean;
  min_age?: number;
  max_age?: number;
}

interface IModality {
  data: IModalityData;
  sold?: boolean;
  onChange?(data: ICategoryData[]): void;
}

const Modality: React.FC<IModality> = ({ data, sold, onChange }) => {
  const [categoriesData, setCategoriesData] = useState<ICategoryData[]>([
    {
      input_id: new Date().getTime(),
      has_kit: true,
    },
  ]);

  useEffect(() => {
    if (data.categories) {
      console.log({ categories: data.categories });
      setCategoriesData(data.categories);
    } else {
      const newCategoriesData = [
        {
          input_id: new Date().getTime(),
          has_kit: true,
        },
      ];
      setCategoriesData(newCategoriesData);
      if (onChange) {
        onChange(newCategoriesData);
      }
    }
  }, [data, onChange]);

  const handleAddCategory = useCallback(() => {
    const newCategoriesData = categoriesData.slice();
    newCategoriesData.push({
      input_id: new Date().getTime(),
      has_kit: true,
    });
    if (onChange) {
      onChange(newCategoriesData);
    }
    setCategoriesData(newCategoriesData);
  }, [categoriesData, onChange]);

  const handleRemoveCategory = useCallback(
    (input_id) => {
      const newCategoriesData = categoriesData.filter(
        (modality) => modality.input_id !== input_id
      );
      if (newCategoriesData.length === 0) {
        newCategoriesData.push({
          input_id: new Date().getTime(),
          has_kit: true,
        });
      }
      if (onChange) {
        onChange(newCategoriesData);
      }
      setCategoriesData(newCategoriesData);
    },
    [categoriesData, onChange]
  );

  const handleChangeCategory = useCallback(
    (value, categoryIndex) => {
      const newCategoriesData = categoriesData.slice();
      newCategoriesData[categoryIndex].name = value;
      if (onChange) {
        onChange(newCategoriesData);
      }
      setCategoriesData(newCategoriesData);
    },
    [categoriesData, onChange]
  );

  const handleChangeChild = useCallback(
    (value: boolean, categoryIndex: number) => {
      const newCategoriesData = categoriesData.slice();
      newCategoriesData[categoryIndex].child = value;
      if (onChange) {
        onChange(newCategoriesData);
      }
      setCategoriesData(newCategoriesData);
    },
    [categoriesData, onChange]
  );

  const handleChangeElderly = useCallback(
    (value: boolean, categoryIndex: number) => {
      const newCategoriesData = categoriesData.slice();
      newCategoriesData[categoryIndex].elderly = value;
      if (onChange) {
        onChange(newCategoriesData);
      }
      setCategoriesData(newCategoriesData);
    },
    [categoriesData, onChange]
  );

  const handleChangeMale = useCallback(
    (value: boolean, categoryIndex: number) => {
      const newCategoriesData = categoriesData.slice();
      newCategoriesData[categoryIndex].male = value;
      if (onChange) {
        onChange(newCategoriesData);
      }
      setCategoriesData(newCategoriesData);
    },
    [categoriesData, onChange]
  );

  const handleChangeFemale = useCallback(
    (value: boolean, categoryIndex: number) => {
      const newCategoriesData = categoriesData.slice();
      newCategoriesData[categoryIndex].female = value;
      if (onChange) {
        onChange(newCategoriesData);
      }
      setCategoriesData(newCategoriesData);
    },
    [categoriesData, onChange]
  );

  const handleChangeQtyMin = useCallback(
    (value: number, categoryIndex: number) => {
      const newCategoriesData = categoriesData.slice();
      newCategoriesData[categoryIndex].qty_min = value;
      if (onChange) {
        onChange(newCategoriesData);
      }
      setCategoriesData(newCategoriesData);
    },
    [categoriesData, onChange]
  );

  const handleChangeHasAgeGroup = useCallback(
    (value: boolean, categoryIndex: number) => {
      const newCategoriesData = categoriesData.slice();
      newCategoriesData[categoryIndex].has_age_group = value;
      if (onChange) {
        onChange(newCategoriesData);
      }
      setCategoriesData(newCategoriesData);
    },
    [categoriesData, onChange]
  );

  const handleChangeHasKit = useCallback(
    (value: boolean, categoryIndex: number) => {
      const newCategoriesData = categoriesData.slice();
      newCategoriesData[categoryIndex].has_kit = value;
      if (onChange) {
        onChange(newCategoriesData);
      }
      setCategoriesData(newCategoriesData);
    },
    [categoriesData, onChange]
  );

  const handleChangeMinAge = useCallback(
    (value: number, categoryIndex: number) => {
      const newCategoriesData = categoriesData.slice();
      newCategoriesData[categoryIndex].min_age = value;
      if (onChange) {
        onChange(newCategoriesData);
      }
      setCategoriesData(newCategoriesData);
    },
    [categoriesData, onChange]
  );

  const handleChangeMaxAge = useCallback(
    (value: number, categoryIndex: number) => {
      const newCategoriesData = categoriesData.slice();
      newCategoriesData[categoryIndex].max_age = value;
      if (onChange) {
        onChange(newCategoriesData);
      }
      setCategoriesData(newCategoriesData);
    },
    [categoriesData, onChange]
  );

  const handleChangeOrder = useCallback(
    (result) => {
      if (!result.destination) return;
      const reordered = categoriesData.slice();
      const [removed] = reordered.splice(result.source.index, 1);
      reordered.splice(result.destination.index, 0, removed);
      if (onChange) {
        onChange(reordered);
      }
      setCategoriesData(reordered);
    },
    [categoriesData, onChange]
  );

  return (
    <Container className="mb-3">
      <div className="bg-category p-3 mb-2">
        <h5 className="roboto h5 fw-semibold">{data.name}</h5>
        <p className="mb-0">
          Mínimo de 1 categoria <MdCheck size={16} color="#8c8c8c" />
        </p>
      </div>
      <DragDropContext onDragEnd={handleChangeOrder}>
        <Droppable droppableId={`categories-${data.input_id}`}>
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              {categoriesData.map((category, index) => (
                <Draggable
                  key={category.input_id}
                  draggableId={category.input_id.toString()}
                  index={index}
                >
                  {(draggableProvided) => (
                    <div
                      ref={draggableProvided.innerRef}
                      {...draggableProvided.draggableProps}
                      {...draggableProvided.dragHandleProps}
                    >
                      <Category
                        sold={sold}
                        category={category}
                        onChangeCategory={(value) =>
                          handleChangeCategory(value, index)
                        }
                        onChangeChild={(value) =>
                          handleChangeChild(value, index)
                        }
                        onChangeElderly={(value) =>
                          handleChangeElderly(value, index)
                        }
                        onChangeMale={(value) => handleChangeMale(value, index)}
                        onChangeFemale={(value) =>
                          handleChangeFemale(value, index)
                        }
                        onRemoveCategory={() =>
                          handleRemoveCategory(category.input_id)
                        }
                        onChangeQtyMin={(value) =>
                          handleChangeQtyMin(value, index)
                        }
                        onChangeHasAgeGroup={(value) =>
                          handleChangeHasAgeGroup(value, index)
                        }
                        onChangeHasKit={(value) =>
                          handleChangeHasKit(value, index)
                        }
                        onChangeMinAge={(value) =>
                          handleChangeMinAge(value, index)
                        }
                        onChangeMaxAge={(value) =>
                          handleChangeMaxAge(value, index)
                        }
                        value={category.name}
                        child={category.child}
                        elderly={category.elderly}
                        male={category.male}
                        female={category.female}
                        qty_min={category.qty_min}
                        has_age_group={category.has_age_group}
                        min_age={category.min_age}
                        max_age={category.max_age}
                        has_kit={category.has_kit}
                      />
                      {console.log({ hasKit: category.has_kit })}
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      <button
        type="button"
        className="d-flex justify-content-center align-items-center rounded bd-add-new-cat bg-transparent p-2 w-100"
        onClick={handleAddCategory}
      >
        <p className="h6 fw-normal btn-add-modality border-0 d-flex align-items-center mb-0">
          Adicionar nova categoria
        </p>
        <div className="border-0 bg-transparent h-100">
          <HiPlus size={16} color="#ff6900" className="ms-2 " />{' '}
        </div>
      </button>
    </Container>
  );
};

export default Modality;
